import React from 'react';
import { Box } from '@mui/material';
import { motion, useReducedMotion } from 'framer-motion';

// Shared list-row card wrapper for the new design language: a rounded white card with a 5px left
// accent strip, hover elevation, and a staggered, reduced-motion-safe entrance. The inner layout
// (grid columns, flex row, etc.) is domain-specific and stays in the page — passed in as children.
// Reproduced byte-for-byte from the former inline row wrappers in both pages.
//
// `accent` colours the left strip. `index` drives the staggered entrance delay.
const ListRowCard: React.FC<{
    accent: string;
    children: React.ReactNode;
    index?: number;
}> = ({ accent, children, index = 0 }) => {
    const reduceMotion = useReducedMotion();

    return (
        <Box
            component={motion.div}
            initial={reduceMotion ? false : { opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.28, delay: Math.min(index * 0.04, 0.2), ease: [0.16, 1, 0.3, 1] }}
            sx={{
                position: 'relative', overflow: 'hidden', borderRadius: '14px', bgcolor: 'background.paper',
                border: '1px solid #ECECE4',
                boxShadow: '0 1px 2px rgba(26,20,15,0.04)',
                transition: 'box-shadow 180ms, background-color 180ms',
                '&::before': {
                    content: '""', position: 'absolute', left: 0, top: 0, bottom: 0, width: 5,
                    bgcolor: accent,
                },
                '&:hover': {
                    bgcolor: '#faf8f3',
                    boxShadow: '0 1px 2px rgba(26,20,15,0.04), 0 10px 28px rgba(26,20,15,0.08)',
                },
            }}
        >
            {children}
        </Box>
    );
};

export default ListRowCard;
